import { createSelector } from 'reselect';
import pick from 'lodash-es/pick';
import pickBy from 'lodash-es/pickBy';
import identity from 'lodash-es/identity';
import isNil from 'lodash-es/isNil';
import isEqual from 'lodash-es/isEqual';
import isEmpty from 'lodash-es/isEmpty';
import mapValues from 'lodash-es/mapValues';

import { normalizeDate } from '@ha/date';
import { TypeValue } from 'ha/types/SearchFilters';

import getPageInfo, { getNumberOfListings } from './getPageInfo';
import getImagesSize from './getPreviewImagesSize';
import transformFiltersToSearchParamsSubset from '../utils/transformFiltersToSearchParamsSubset';
// eslint-disable-next-line import/no-cycle
import {
  getFurnitureFilter as getFurniture,
  getBillsFilter as getBills,
  getCategoriesFilter as getCategories,
  getCollectionFilter as getCollection,
  getRegistrationFilter as getRegistration,
  getRulesFilter as getRules,
  getSuitableForFilter as getSuitableFor,
  getRecentlyAddedFilter as getRecentlyAdded,
  getGenderFilter as getGender,
} from '../reducer';
import {
  STATE_NAME,
  PriceDistributionSteps,
  RequiredAnalyticsFilters,
} from '../constants';
import { getUniversities } from './searchSelectors';

import * as mapSelectors from './mapSelectors';

import {
  getCurrencyRates,
  getPriceFilterCurrency,
  getPriceFilterDefaultCurrency,
} from './priceSelectors';

import {
  getFilterValues,
  getIsSearchAreaFilterHidden,
} from './filterSelectors';

import { getListings, getIsSearchEmpty } from './listingsSelectors';

import { getCityCanonical, getCountryCode } from './loadStateSelectors';

export { getPageInfo, getNumberOfListings };
export * from './getRequestCache';

export {
  getCurrentIndex,
  getCurrentSorting,
  getSortingOptions,
} from './sortingSelectors';

export {
  getPriceFilterDefaultCurrency,
  getCurrencyRates,
  getListings,
  getIsSearchEmpty,
  getCityCanonical,
  getCountryCode,
  getUniversities,
};

const getSearch = state => state[STATE_NAME];

export const getFurnitureFilter = createSelector(getSearch, getFurniture);
export const getBillsFilter = createSelector(getSearch, getBills);
export const getCategoriesFilter = createSelector(getSearch, getCategories);
export const getCollectionFilter = createSelector(getSearch, getCollection);
export const getRegistrationFilter = createSelector(getSearch, getRegistration);
export const getRulesFilter = createSelector(getSearch, getRules);
export const getSuitableForFilter = createSelector(getSearch, getSuitableFor);
export const getRecentlyAddedFilter = createSelector(
  getSearch,
  getRecentlyAdded,
);
export const getGenderFilter = createSelector(getSearch, getGender);
export const getlangURLListState = createSelector(
  getSearch,
  search => search.langURLList,
);
export const getCitiesNearbyState = createSelector(
  getSearch,
  search => search.citiesNearby,
);
export const getDateFilter = createSelector(
  getSearch,
  search => search.dateFilter,
);
export const getMapLocation = mapSelectors.getLocation;

export const getCityName = mapSelectors.getCityLocalized;
export const getCountryName = mapSelectors.getCountryLocalized;
export const getCitiesNearby = createSelector(
  getCitiesNearbyState,
  cities => cities.citiesNearby,
);
export const hasCitiesNearby = createSelector(
  getCitiesNearbyState,
  citiesNearby =>
    Boolean(citiesNearby.citiesNearby && citiesNearby.citiesNearby.length > 0),
);

export const getIsAdvertiserSearch = createSelector(getPageInfo, pageInfo =>
  Boolean(pageInfo.userId),
);

export const getAdvertiserName = createSelector(
  getPageInfo,
  pageInfo => pageInfo.advertiserName,
);

const getLoadState = createSelector(getSearch, search => search.loadState);

export const getCountryCanonical = createSelector(
  getLoadState,
  loadState => loadState.countryCanonical,
);
export const getCityLocalized = createSelector(
  getLoadState,
  loadState => loadState.cityLocalized,
);
export const getCountryLocalized = createSelector(
  getLoadState,
  loadState => loadState.countryLocalized,
);
export const getCitySearch = createSelector(
  getLoadState,
  loadState => loadState.citySearch,
);
export const getErrorState = createSelector(
  getLoadState,
  loadState => loadState.error,
);
export const isNotFound = createSelector(
  getLoadState,
  loadState => loadState.notFound,
);
export const isUnsupportedCountry = createSelector(
  getLoadState,
  loadState => loadState.unsupportedCountry,
);
export const getIsLoading = createSelector(
  getLoadState,
  loadState => loadState.loading,
);

export const getListingsSummary = createSelector(
  getSearch,
  search => search.listingsSummary,
);

export const getAnalyticsForListings = createSelector(
  [getPageInfo, getListings, getListingsSummary],
  (pageInfo, listings, { organic, partner, isExhaustive }) => ({
    numberOfPartnerListings: partner,
    numberOfOrganicListings: organic,
    listingIdResults: listings.map(({ internalID }) => internalID),
    numberOfSearchResults: pageInfo.total,
    isNumberOfSearchResultsExhaustive: pageInfo.isExhaustive,
    areCountersOfOrganicAndPartnerListingsExhaustive: isExhaustive,
  }),
);

export const getLocation = mapSelectors.getSearchQuery;

export const getDateFilterValues = createSelector([getDateFilter], filters =>
  mapValues(pick(filters, ['startDate', 'endDate']), normalizeDate),
);

export const isMobileMapOpen = mapSelectors.getIsMobileMapOpen;

export const {
  getMapViewState,
  getMapVisitedRooms,
  isMarkerVisited,
  isMarkerFavorite,
  getTravelTimePolygon,
  getTravelTimeCurrentInfo,
  getTravelTimeIsLoading,
  getUseMapAsAFilter,
  getBounds,
  getTotalNumberOfMarkers,
} = mapSelectors;

export const isSearchByPartner = createSelector(
  mapSelectors.getUniId,
  uniId => !!uniId,
);

export const getCitiesNearbyFilter = createSelector(
  [getCitiesNearbyState, getCitySearch],
  ({ limit, minPopulation, minRooms }, citySearch) => ({
    citySearch,
    cityLimit: limit,
    cityMinPopulation: minPopulation,
    cityMinRooms: minRooms,
  }),
);

export const shouldUseMapBounds = createSelector(
  [getUseMapAsAFilter, getIsSearchAreaFilterHidden],
  (useMapAsAFilter, isSearchAreaFilterHidden) =>
    useMapAsAFilter && !isSearchAreaFilterHidden,
);

export const getSearchParams = createSelector(
  [
    getFilterValues,
    getPageInfo,
    getCitiesNearbyFilter,
    getImagesSize,
    getCitySearch,
  ],
  (filterValues, pageInfo, citiesNearbyFilter, imagesSize, citySearch) => {
    const reqParams = {
      ...transformFiltersToSearchParamsSubset(filterValues),
      ...citiesNearbyFilter,

      citySearch,
      imagesSize,

      offset: pageInfo.offset,
      limit: pageInfo.limit,
      advertiserId: pageInfo.userId,

      // Constants
      priceDistributionSteps: PriceDistributionSteps,
    };

    return pickBy(reqParams, val => !isNil(val));
  },
);

export const getMapParams = createSelector(
  [
    mapSelectors.getLatitude,
    mapSelectors.getLongitude,
    mapSelectors.getZoomLevel,
  ],
  (latitude, longitude, zoomLevel) => ({
    latitude,
    longitude,
    zoomLevel,
  }),
);

export const getInitialMapParams = createSelector(
  [
    mapSelectors.getInitialLatitude,
    mapSelectors.getInitialLongitude,
    mapSelectors.getInitialZoomLevel,
  ],
  (initialLatitude, initialLongitude, initialZoomLevel) => ({
    initialLatitude,
    initialLongitude,
    initialZoomLevel,
  }),
);

export const getAnalyticsForFilters = createSelector(
  [
    getAnalyticsForListings,
    getSearchParams,
    getMapParams,
    getInitialMapParams,
    getCurrencyRates,
    getPriceFilterCurrency,
    getCityCanonical,
    getIsSearchAreaFilterHidden,
    getUseMapAsAFilter,
  ],
  (
    listingsAnalytics,
    searchParams,
    mapParams,
    initialMapParams,
    currencyRates,
    currency,
    cityCanonical,
    isSearchAreaFilterHidden,
    useMapAsAFilter,
    // eslint-disable-next-line max-params
  ) => {
    const currencyRate = currencyRates[currency];

    const normalizePrice = amount => Math.round(amount / 100 / currencyRate);

    const normalizeDateToISO = date => new Date(date).toISOString();

    const filters = pickBy(
      pick(searchParams, Object.keys(RequiredAnalyticsFilters)),
      identity,
    );

    if (filters.priceMax) {
      filters.priceMax = normalizePrice(filters.priceMax);
    }

    if (filters.priceMin) {
      filters.priceMin = normalizePrice(filters.priceMin);
    }

    if (filters.startDate) {
      const normalizedDate = normalizeDateToISO(filters.startDate);

      filters.startDate = normalizedDate;
      filters.startDate2 = normalizedDate;
    }

    if (filters.endDate) {
      const normalizedDate = normalizeDateToISO(filters.endDate);

      filters.endDate = normalizedDate;
      filters.endDate2 = normalizedDate;
    }

    const filterTypes = Object.keys(
      pickBy(
        filters,
        (val, key) => !isEqual(val, RequiredAnalyticsFilters[key]),
      ),
    ).filter(type => type !== 'searchArea');

    const { zoomLevel } = mapParams;
    const { initialZoomLevel } = initialMapParams;

    if (!isSearchAreaFilterHidden && useMapAsAFilter) {
      filterTypes.push('mapBoundaries');
    }
    if (useMapAsAFilter && zoomLevel && zoomLevel !== initialZoomLevel) {
      filterTypes.push('zoomLevel');
    }

    if (!isEmpty(filters) || searchParams.topLat) {
      return {
        cityCanonical,
        currency: searchParams.currency,
        ...listingsAnalytics,
        ...RequiredAnalyticsFilters,
        ...filters,
        filterTypes,
      };
    }

    return {};
  },
);

export const getUniversity = createSelector(
  [mapSelectors.getUniId, getUniversities],
  (uniId, universities) =>
    universities.find(({ entityId }) => entityId === uniId) || null,
);

export const getPageName = createSelector(
  [getUniversity, getCollectionFilter],
  (university, category) => {
    if (university) {
      return university.name;
    }

    return {
      [TypeValue.APARTMENT]: 'Apartments',
      [TypeValue.PRIVATE_ROOM]: 'Private Rooms',
      [TypeValue.SHARED_ROOM]: 'Shared Rooms',
      [TypeValue.STUDIO]: 'Studios',
      '': 'All accommodation',
    }[category];
  },
);

export { default as getPagerProps } from './getPagerProps';

export const getPriceAverage = createSelector(
  getLoadState,
  loadState => loadState.priceAverage,
);

const getCity = createSelector(getSearch, search => search.city);

export const getCityCategories = createSelector(
  getCity,
  city => city.categories,
);
